import { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { musicServicesByMusicAPIID } from 'fym-shared/src/music-services/allMusicServices';
import { artistHomeMessages as messages } from './artistHomeMessages';
import { AudienceTileModel } from './audienceTiles';
import { HomeTileDelta, countDelta, formatSignedPercent } from './homeDeltas';

export interface AudienceTileText {
  label: string;
  source: string;
  delta: HomeTileDelta;
}

/** The words of an Audience tile: "Spotify followers", "YouTube subscribers", the source line, the badge. */
export function useAudienceTileText(): (tile: AudienceTileModel) => AudienceTileText {
  const intl = useIntl();

  return useCallback(
    (tile: AudienceTileModel) => {
      const service = musicServicesByMusicAPIID[tile.integrationType]?.name ?? tile.integrationType;
      const label = (() => {
        switch (tile.labelKind) {
          case 'subscribers':
            return intl.formatMessage(messages.kpiSubscribers, { service });
          case 'fans':
            return intl.formatMessage(messages.kpiFans, { service });
          case 'monthlyListeners':
            return intl.formatMessage(messages.kpiMonthlyListeners);
          case 'followers':
            return intl.formatMessage(messages.kpiFollowers, { service });
          default:
            return intl.formatMessage(messages.kpiFollowers, { service });
        }
      })();
      const source =
        tile.source === 'spotifyForArtists'
          ? intl.formatMessage(messages.sourceSpotifyForArtists)
          : intl.formatMessage(messages.sourceProfile);

      let delta: HomeTileDelta;
      if (tile.delta.mode === 'count') {
        delta = countDelta(intl, tile.value, tile.delta.periodDelta);
      } else if (tile.delta.changePct === null) {
        delta = { kind: 'firstWeek' };
      } else {
        delta = { kind: 'delta', value: tile.delta.changePct, label: formatSignedPercent(intl, tile.delta.changePct) };
      }
      return { label, source, delta };
    },
    [intl]
  );
}
